<template>
  <CustomModal ref="modal" title="Publication" class="publication-modal">
    <div class="container">
      <template v-if="publication">
        <div class="summary">
          <span class="topic-title" :title="topic?.title">{{ topic?.title }}</span>
          <FunnelStageBadge v-if="topic" :stage="topic.funnelStage" />
          <StatusBadge :status="publication.status" :status-enum="PUBLICATION_STATUS_ENUM" :label="channelLabel + ' · ' + statusLabel" />
          <span v-if="isDirty" class="dirty">Modifications non enregistrées</span>
        </div>

        <div class="tabs" role="tablist">
          <button v-for="item in tabs" :key="item.key" type="button" role="tab" class="tab" :class="{active: tab === item.key}" :aria-selected="tab === item.key"
                  :disabled="item.disabled" :title="item.disabled ? item.disabledHint : undefined" @click="tab = item.key">{{ item.label }}</button>
        </div>

        <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
          <PublicationWriting v-show="tab === 'writing'" :publication=publication :topic=topic :inputs=inputs :templates=templates :default-template-id=defaultTemplateId
                              :copy-prompt=copyPrompt :apply-response=applyResponse />
          <PublicationImage v-show="tab === 'image'" :publication=publication :inputs=inputs :set-required=setImageRequired :set-status=setImageStatus
                            :upload=uploadImage :remove-file=removeImage />
          <PublicationBroadcast v-show="tab === 'broadcast'" :publication=publication :topic=topic :inputs=inputs :schedule=schedule :unschedule=unschedule
                                :publish=publish :unpublish=unpublish :copy-field=copyField />
          <PublicationResults v-if="isPublished" v-show="tab === 'results'" :publication=publication :metric-inputs=metricInputs />

          <div class="button-container">
            <CustomButton class="button" @click="submitForm" :params=buttons.submit />
            <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
          </div>
        </CustomForm>
      </template>
      <p v-else class="loading">Chargement…</p>
    </div>
  </CustomModal>
  <ConfirmModal ref="confirmModal" :title=confirm.title :text=confirm.text yes="Confirmer" no="Retour" />
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import {NotificationService} from "@vues/v-common";
import {PublicationController} from "@/marketing/controllers/PublicationController";
import {TopicController} from "@/marketing/controllers/TopicController";
import {AssetController} from "@/marketing/controllers/AssetController";
import {PromptController} from "@/marketing/controllers/PromptController";
import {SettingsController} from "@/marketing/controllers/SettingsController";
import FunnelStageBadge from "@/marketing/components/FunnelStageBadge.vue";
import PublicationWriting from "@/marketing/components/publication/PublicationWriting.vue";
import PublicationImage from "@/marketing/components/publication/PublicationImage.vue";
import PublicationBroadcast from "@/marketing/components/publication/PublicationBroadcast.vue";
import PublicationResults from "@/marketing/components/publication/PublicationResults.vue";
import {
  CHANNEL_ENUM, METRICS, PUBLICATION_STATUS_ENUM, copyText, fromInputDate, hashtagsToText, idOf, labelOf, parseHashtags, toInputDateTime
} from "@/marketing/utils";

const MAX_CHECKS = 30;
const MAX_CHECK_LENGTH = 200;

/**
 * Modale d'une publication (un sujet sur un réseau) : openModal(identifiant de la publication, onglet facultatif).
 * Onglets Rédaction, Image, Diffusion et Résultats. Le texte, le commentaire, les hashtags, le lien et les résultats sont enregistrés avec le bouton
 * « Enregistrer » ; l'image, la programmation et la publication agissent tout de suite (et enregistrent d'abord le reste).
 * callback est appelé après chaque modification, closedCallback quand la modale se referme.
 */
export default {
  name: 'PublicationModal',
  props: ['callback', 'closedCallback'],
  components: {CustomModal, CustomForm, CustomButton, ConfirmModal, StatusBadge, FunnelStageBadge, PublicationWriting, PublicationImage, PublicationBroadcast, PublicationResults},
  data() {
    return {
      PUBLICATION_STATUS_ENUM,
      publicationId: undefined,
      publication: undefined,
      topic: undefined,
      templates: [],
      defaultTemplateId: undefined,
      tab: 'writing',
      saved: '',
      confirm: {title: '', text: ''},
      inputs: {
        text: {label: 'Texte du post', value: '', type: 'text'},
        textStatus: {value: 'DRAFT'},
        firstComment: {label: 'Premier commentaire', value: '', type: 'text'},
        hashtags: {label: 'Hashtags (facultatif)', value: '', type: 'text', placeholder: '#facturation #tva'},
        templateId: {value: ''},
        imageBrief: {label: 'Description de l\'image', value: '', type: 'text'},
        scheduledAt: {label: 'Date et heure', value: '', type: 'datetime-local'},
        publishedAt: {label: 'Publié le', value: '', type: 'datetime-local'},
        externalUrl: {label: 'Lien du post', value: '', type: 'text', placeholder: 'https://www.linkedin.com/posts/…'},
      },
      metricInputs: Object.fromEntries(METRICS.map(metric => [metric.key, {label: metric.label, value: '', type: 'number', step: 1}])),
      buttons: {
        submit: {text: "Enregistrer"},
        cancel: {text: "Fermer"},
      }
    };
  },
  computed: {
    isPublished() {
      return this.publication?.status === PUBLICATION_STATUS_ENUM.PUBLISHED.value;
    },
    channelLabel() {
      return labelOf(CHANNEL_ENUM, this.publication?.channel, '');
    },
    statusLabel() {
      return labelOf(PUBLICATION_STATUS_ENUM, this.publication?.status, '');
    },
    tabs() {
      return [
        {key: 'writing', label: 'Rédaction'},
        {key: 'image', label: 'Image'},
        {key: 'broadcast', label: 'Diffusion'},
        {key: 'results', label: 'Résultats', disabled: !this.isPublished, disabledHint: 'Disponible une fois la publication publiée'},
      ];
    },
    isDirty() {
      return this.publication !== undefined && this.snapshot() !== this.saved;
    },
  },
  mounted() {
    // La modale peut se fermer par sa croix ou la touche Échap : on s'abonne à son état pour prévenir le tableau et vider la fiche
    this.$watch(() => this.$refs.modal?.isDisplayed, open => {
      if (open === false) this.onClosed();
    });
  },
  methods: {
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    notify(type, message) {
      (new NotificationService).addNotification(type, message);
    },
    text(input) {
      return String(input.value ?? '').trim();
    },
    onClosed() {
      this.publication = undefined;
      this.publicationId = undefined;
      if (this.closedCallback) this.closedCallback();
    },
    // Ce que l'utilisateur peut avoir modifié sans l'enregistrer
    snapshot() {
      return JSON.stringify([
        this.inputs.text.value, this.inputs.textStatus.value, this.inputs.firstComment.value, this.inputs.hashtags.value, this.inputs.templateId.value,
        this.inputs.imageBrief.value, this.inputs.externalUrl.value, this.inputs.publishedAt.value,
        METRICS.map(metric => this.metricInputs[metric.key].value),
      ]);
    },
    fill() {
      const publication = this.publication;
      this.inputs.text.value = publication.text?.content ?? '';
      this.inputs.textStatus.value = publication.text?.status === 'FINAL' ? 'FINAL' : 'DRAFT';
      this.inputs.firstComment.value = publication.firstComment ?? '';
      this.inputs.hashtags.value = hashtagsToText(publication.hashtags);
      this.inputs.templateId.value = publication.promptTemplateId ?? '';
      this.inputs.imageBrief.value = publication.image?.brief ?? '';
      this.inputs.scheduledAt.value = toInputDateTime(publication.scheduledAt);
      this.inputs.publishedAt.value = toInputDateTime(publication.publishedAt);
      this.inputs.externalUrl.value = publication.externalUrl ?? '';
      METRICS.forEach(metric => {
        const value = publication.metrics?.[metric.key];
        this.metricInputs[metric.key].value = typeof value === 'number' ? value : '';
      });
      this.saved = this.snapshot();
    },
    normalize(publication) {
      return {...publication, id: idOf(publication), image: {required: false, status: 'NOT_REQUIRED', files: [], ...(publication.image ?? {})}};
    },
    // Recharge la publication et son sujet sans toucher aux champs en cours de saisie
    async reloadPublication() {
      const publication = await PublicationController.get(this.publicationId);
      if (!publication || publication.errors) return false;
      this.publication = this.normalize(publication);
      const topic = await TopicController.get(publication.topicId);
      if (topic && !topic.errors) this.topic = topic;
      return true;
    },
    // Recharge tout, champs compris
    async refresh() {
      if (!(await this.reloadPublication())) return false;
      this.fill();
      return true;
    },
    async openModal(publicationId, tab = 'writing') {
      this.publication = undefined;
      this.topic = undefined;
      this.publicationId = publicationId;
      this.tab = tab;
      this.$refs.modal.openModal();

      const [loaded, templates, settings] = await Promise.all([
        this.refresh(), this.templates.length > 0 ? this.templates : PromptController.getTemplates(), SettingsController.get()
      ]);
      if (!loaded) {
        this.closeModal();
        return;
      }
      if (Array.isArray(templates)) this.templates = templates;
      this.defaultTemplateId = this.chooseDefaultTemplate(settings);
    },
    // Même ordre que le service : modèle du groupe, sinon celui conseillé pour l'intention du sujet, sinon le premier du catalogue
    chooseDefaultTemplate(settings) {
      const groupDefault = settings && !settings.errors ? settings.defaultPromptTemplateId : undefined;
      if (groupDefault && this.templates.some(template => template.id === groupDefault)) return groupDefault;
      const suggested = this.templates.find(template => (template.suggestedFor ?? []).includes(this.topic?.funnelStage));
      return (suggested ?? this.templates[0])?.id;
    },
    closeModal() {
      this.$refs.modal?.closeModal();
    },
    buildBody() {
      const content = this.text(this.inputs.text);
      const body = {
        text: content ? {content, status: this.inputs.textStatus.value} : {content: ''},
        firstComment: this.text(this.inputs.firstComment),
        hashtags: parseHashtags(this.inputs.hashtags.value),
        image: {brief: this.text(this.inputs.imageBrief)},
        externalUrl: this.text(this.inputs.externalUrl),
        promptTemplateId: this.inputs.templateId.value || '',
      };
      // La date de publication n'existe que pour une publication publiée ; vide, elle devient « Date non définie »
      if (this.isPublished) body.publishedAt = fromInputDate(this.inputs.publishedAt.value);
      return body;
    },
    buildMetrics() {
      return Object.fromEntries(METRICS.map(metric => {
        const value = this.metricInputs[metric.key].value;
        return [metric.key, value === '' || value === undefined || value === null ? null : Number(value)];
      }));
    },
    // Les résultats saisis diffèrent de ceux enregistrés (la date de relevé ne change que dans ce cas)
    metricsChanged() {
      return METRICS.some(metric => {
        const value = this.metricInputs[metric.key].value;
        const entered = value === '' || value === undefined || value === null ? undefined : Number(value);
        return this.publication.metrics?.[metric.key] !== entered;
      });
    },
    // Enregistre les champs saisis ; renvoie true si tout est enregistré
    async save({silent = false} = {}) {
      if (!this.publication) return false;
      const res = await this.$refs.form.submitForm(() => PublicationController.patch(this.publicationId, this.buildBody()));
      if (!res || res.errors) return false;

      if (this.isPublished && this.metricsChanged()) {
        const metrics = await PublicationController.saveMetrics(this.publicationId, this.buildMetrics());
        if (!metrics || metrics.errors) return false;
      }
      await this.refresh();
      if (!silent) this.notify("success", "Publication enregistrée");
      if (this.callback) await this.callback(this.publicationId);
      return true;
    },
    submitForm(event) {
      this.preventNativeSubmit(event);
      return this.save();
    },
    // Avant une action immédiate : ce qui est saisi est enregistré d'abord, pour ne rien perdre au rechargement
    async saveIfDirty() {
      return !this.isDirty || await this.save({silent: true});
    },
    async changed() {
      if (this.callback) await this.callback(this.publicationId);
    },

    // ---- Rédaction
    async copyPrompt(templateId) {
      if (!(await this.saveIfDirty())) return;
      const res = await PromptController.build(this.publicationId, templateId);
      if (!res || res.errors) return;
      const copied = await copyText(res.prompt);
      this.notify(copied ? "success" : "danger", copied ? `Prompt copié (${res.template.label}) : collez-le dans ChatGPT` : "Impossible de copier le prompt");
    },
    // Range la réponse découpée dans les champs, ajoute les points à vérifier au sujet, puis enregistre
    async applyResponse(parts) {
      if (this.text(this.inputs.text) && parts.post) {
        this.confirm = {title: "Remplacement", text: "Le texte actuel sera remplacé par celui de la réponse."};
        if (!(await this.$refs.confirmModal.displayChoice())) return;
      }
      if (parts.post) {
        this.inputs.text.value = parts.post;
        this.inputs.textStatus.value = 'DRAFT';
      }
      if (parts.firstComment) this.inputs.firstComment.value = parts.firstComment;
      if (parts.image) {
        this.inputs.imageBrief.value = parts.image;
        if (!this.publication.image.required && !(await this.setImageRequired(true, true))) return;
      }
      const added = await this.addChecks(parts.checks ?? []);
      if (!(await this.save({silent: true}))) return;
      this.notify("success", `Réponse répartie${added > 0 ? ` · ${added} point${added > 1 ? 's' : ''} à vérifier ajouté${added > 1 ? 's' : ''} au sujet` : ''}`);
    },
    // Ajoute au sujet les points à vérifier qu'il n'a pas déjà ; renvoie leur nombre
    async addChecks(labels) {
      const current = (this.topic?.checks ?? []).map(check => ({label: check.label, done: check.done === true}));
      const known = new Set(current.map(check => check.label.toLowerCase()));
      const fresh = labels.map(label => label.slice(0, MAX_CHECK_LENGTH)).filter(label => label && !known.has(label.toLowerCase())).slice(0, Math.max(0, MAX_CHECKS - current.length));
      if (fresh.length === 0) return 0;
      const res = await TopicController.patch(idOf(this.topic), {checks: [...current, ...fresh.map(label => ({label, done: false}))]});
      if (!res || res.errors) return 0;
      await this.reloadPublication();
      return fresh.length;
    },

    // ---- Image
    async patchImage(image, message) {
      const res = await PublicationController.patch(this.publicationId, {image});
      if (!res || res.errors) return false;
      await this.reloadPublication();
      if (message) this.notify("success", message);
      await this.changed();
      return true;
    },
    setImageRequired(required, silent = false) {
      if (this.publication.image.required === required) return true;
      return this.patchImage({required}, silent ? undefined : (required ? "Image demandée" : "Publication sans image"));
    },
    setImageStatus(status) {
      return this.patchImage({status});
    },
    async uploadImage(file) {
      const res = await AssetController.upload(this.publicationId, file);
      if (!res || res.errors) return false;
      await this.reloadPublication();
      this.notify("success", "Image ajoutée");
      await this.changed();
      return true;
    },
    async removeImage(file) {
      this.confirm = {title: "Suppression", text: `Retirer l'image « ${file.name} » ?`};
      const res = await this.$refs.confirmModal.displayChoice(() => AssetController.delete(this.publicationId, file.id));
      if (res === false || (res && res.errors)) return;
      await this.reloadPublication();
      this.notify("success", "Image retirée");
      await this.changed();
    },

    // ---- Diffusion
    async copyField(field) {
      const value = field === 'text' ? this.text(this.inputs.text) : this.text(this.inputs.firstComment);
      const copied = await copyText(value);
      this.notify(copied ? "success" : "danger", copied ? (field === 'text' ? "Texte copié" : "Premier commentaire copié") : "Impossible de copier");
    },
    // Exécute une action de diffusion : saisie enregistrée d'abord, puis rechargement complet (le statut et les dates changent)
    async broadcast(action, message) {
      if (!(await this.saveIfDirty())) return;
      const res = await action();
      if (!res || res.errors) return;
      await this.refresh();
      this.notify("success", message);
      await this.changed();
    },
    schedule() {
      const scheduledAt = fromInputDate(this.inputs.scheduledAt.value);
      if (!scheduledAt) {
        this.notify("danger", "Choisissez la date et l'heure de publication");
        return;
      }
      return this.broadcast(() => PublicationController.schedule(this.publicationId, {scheduledAt}), "Publication programmée");
    },
    unschedule() {
      return this.broadcast(() => PublicationController.unschedule(this.publicationId), "Publication déprogrammée");
    },
    publish() {
      const body = {};
      const publishedAt = fromInputDate(this.inputs.publishedAt.value);
      if (publishedAt) body.publishedAt = publishedAt;
      if (this.text(this.inputs.externalUrl)) body.externalUrl = this.text(this.inputs.externalUrl);
      return this.broadcast(() => PublicationController.publish(this.publicationId, body), "Publication marquée comme publiée");
    },
    async unpublish() {
      this.confirm = {title: "Annuler la publication", text: "La publication redevient un brouillon. Sa date, son lien et ses résultats saisis sont effacés."};
      if (!(await this.$refs.confirmModal.displayChoice())) return;
      await this.broadcast(() => PublicationController.unpublish(this.publicationId), "Publication annulée");
      this.tab = 'broadcast';
    },
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/marketing/style/modal.styl';

.container
  display flex
  flex-direction column
  min-height 540px

// Rappel du sujet : titre, intention, réseau et état
.summary
  display flex
  align-items center
  flex-wrap wrap
  gap 10px
  padding 0 4px

.topic-title
  min-width 0
  max-width 100%
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  font-size 1.05em
  font-weight 700
  color THEME_COLOR_PRIMARY

.dirty
  margin-left auto
  white-space nowrap
  color #b45309
  font-size 0.85em
  font-weight 600

.loading
  margin 40px 0
  text-align center
  color CTRL_TEXT_MUTED
</style>

<style lang="stylus">
.publication-modal
  > .modal
    width 1000px !important
    max-width 100%
    height auto !important
    max-height 96vh
    overflow-y auto
</style>
